/* One dataset's score, in the four readings a diagnosis runs through.
 *
 * A drawer rather than a route, and that is the interaction decision the whole screen rests on: the
 * ledger stays on screen behind it, so a reader who opens the third of six flagged datasets can
 * close it and open the fourth without losing their filter, their sort or their place. A detail
 * *page* would make every comparison a round trip through the browser's back button.
 *
 * The four tabs are the four questions in the order they are asked — what is this number made of,
 * why did it move, which run introduced it, which checks failed and on how many records. Every one
 * of them is built from payloads this platform already stores; none is a summary generated for the
 * occasion.
 *
 * Two reads serve the whole drawer: the dataset's current `Score` and its `Trend`. Everything else
 * — the previous run, the two sides of a comparison — is a `Score` by id, and never more than two
 * of those at once.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useRequestExecution } from '@/features/rules';

import { ApiError } from '@/shared/api/http';
import { Alert } from '@/shared/ui/Alert';
import { Drawer } from '@/shared/ui/Drawer';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { UnavailableState } from '@/shared/ui/States';
import { Tabs } from '@/shared/ui/Tabs';

import { useDatasetScore, useDatasetTrend } from '../api/scoring';
import { formatRatio, formatScore } from '../model/presentation';
import type { DatasetRow, DrawerTab } from '../model/types';

import { Delta } from './cells';
import { FailedChecks } from './FailedChecks';
import { RunComparison } from './RunComparison';
import { ScoreChangeExplanation } from './ScoreChangeExplanation';
import { ScoreDecomposition } from './ScoreDecomposition';
import { STATUS_TONE } from './tone';

/** A dataset no execution has covered answers 404, which is a state rather than a fault. */
const NOT_FOUND = 404;

export interface DatasetDrawerProps {
  readonly row: DatasetRow;
  readonly tab: DrawerTab;
  readonly onTab: (tab: DrawerTab) => void;
  readonly onClose: () => void;
  readonly days: number;
}

export function DatasetDrawer({
  row,
  tab,
  onTab,
  onClose,
  days,
}: DatasetDrawerProps): ReactElement {
  const { t } = useTranslation();
  const score = useDatasetScore(row.id);
  const trend = useDatasetTrend(row.id, days);
  const rerun = useRequestExecution();
  const [pickedNewer, setPickedNewer] = useState<string | undefined>(undefined);
  const [pickedOlder, setPickedOlder] = useState<string | undefined>(undefined);

  const points = useMemo(() => trend.data?.points ?? [], [trend.data]);

  /* Defaults are the two most recent runs, which is the comparison somebody almost always wants.
     Held as "picked or default" rather than seeded into state, so a trend that arrives after the
     drawer opens does not leave the selects empty. */
  const newerId = pickedNewer ?? points[points.length - 1]?.score_id;
  const olderId = pickedOlder ?? points[points.length - 2]?.score_id;
  const previousScoreId = points[points.length - 2]?.score_id;

  const tabs = [
    { id: 'decomposition' as const, label: t('scoring.tab.decomposition') },
    { id: 'change' as const, label: t('scoring.tab.change') },
    { id: 'runs' as const, label: t('scoring.tab.runs'), count: points.length },
    {
      id: 'checks' as const,
      label: t('scoring.tab.checks'),
      count: score.data?.contributions.length,
    },
  ];

  const notScored =
    score.isError && score.error instanceof ApiError && score.error.status === NOT_FOUND;

  const body = ((): ReactElement => {
    if (score.isPending) return <SkeletonRows rows={6} columns={3} />;
    if (notScored) {
      return (
        <EmptyState
          title={t('scoring.decomposition.unscored')}
          guidance={
            row.unscored?.kind === 'never'
              ? t('scoring.drawer.neverProfiled')
              : t('scoring.decomposition.unscoredGuidance')
          }
        />
      );
    }
    if (score.isError) {
      return (
        <UnavailableState
          title={t('scoring.decomposition.unreadable')}
          guidance={score.error.message}
        />
      );
    }
    if (tab === 'decomposition') {
      return <ScoreDecomposition score={score.data} onGoTo={onTab} />;
    }
    if (tab === 'change') {
      return (
        <ScoreChangeExplanation
          score={score.data}
          previousScoreId={previousScoreId}
          onGoTo={onTab}
        />
      );
    }
    if (tab === 'runs') {
      if (trend.isPending) return <SkeletonRows rows={4} columns={4} />;
      if (trend.isError) {
        return (
          <UnavailableState
            title={t('scoring.runs.unreadable')}
            guidance={trend.error.message}
          />
        );
      }
      return (
        <RunComparison
          points={points}
          newerId={newerId}
          olderId={olderId}
          onPick={(id) => {
            /* Picking a run makes it the newer side and demotes the current newer side to the
               older one — "and how does it compare to this" is the gesture somebody makes, where
               "please set slot B" is not. */
            setPickedOlder(newerId);
            setPickedNewer(id);
          }}
          onSetOlder={setPickedOlder}
        />
      );
    }
    return <FailedChecks score={score.data} />;
  })();

  return (
    <Drawer
      title={row.name}
      closeLabel={t('scoring.drawer.close')}
      onClose={onClose}
      meta={
        <div className="lq-drawer__meta">
          <StatusBadge
            tone={STATUS_TONE[row.status]}
            label={t(`scoring.status.${row.status}`)}
          />
          <span className="lq-muted">{row.schema}</span>
          {row.target !== null && (
            <Link className="lq-linkish" to={`/contracts/${row.target.contractId}`}>
              {row.target.contractName}
            </Link>
          )}
          {score.data !== undefined && (
            <span className="lq-muted">
              {t('scoring.score.attribution', {
                version: score.data.score_model_version,
              })}
            </span>
          )}
        </div>
      }
    >
      <div className="lq-drawer__hero">
        <div className="lq-drawer__figure">
          <span className="lq-drawer__figlabel">{t('scoring.drawer.overall')}</span>
          <span className={`lq-drawer__figvalue lq-drawer__figvalue--${row.status}`}>
            {row.value === null ? t('scoring.estate.none') : formatScore(row.value)}
          </span>
        </div>
        <div className="lq-drawer__figure">
          <span className="lq-drawer__figlabel">
            {t('scoring.column.change', { days })}
          </span>
          <span className="lq-drawer__figsmall">
            {row.movement === undefined ? (
              <span className="lq-muted">{t('scoring.movement.unread')}</span>
            ) : (
              <Delta delta={row.movement.delta} />
            )}
          </span>
        </div>
        <div className="lq-drawer__figure">
          <span className="lq-drawer__figlabel">
            {t('scoring.listing.column.coverage')}
          </span>
          <span className="lq-drawer__figsmall">
            {formatRatio(row.coverageRatio) === null
              ? t('scoring.listing.noColumns')
              : t('scoring.coverage.percent', {
                  percent: formatRatio(row.coverageRatio) ?? 0,
                })}
            {row.summary !== undefined && (
              <span className="lq-scores__sub">
                {t('scoring.coverage.columns', {
                  covered: row.summary.columns_with_a_rule,
                  total: row.summary.columns_total,
                })}
              </span>
            )}
          </span>
        </div>
        <div className="lq-drawer__figure">
          <span className="lq-drawer__figlabel">{t('scoring.column.target')}</span>
          <span className="lq-drawer__figsmall">
            {row.target === null ? (
              <span className="lq-muted">{t('scoring.target.none')}</span>
            ) : (
              <>
                {t('scoring.target.atLeast', { target: row.target.minScore })}
                <span className="lq-scores__sub">
                  {row.slack === null
                    ? t('scoring.target.unmeasuredShort')
                    : row.slack >= 0
                      ? t('scoring.target.metShort')
                      : t('scoring.target.shortBy', {
                          points: formatScore(Math.abs(row.slack)),
                        })}
                </span>
              </>
            )}
          </span>
        </div>
        <div className="lq-drawer__figure">
          <span className="lq-drawer__figlabel">{t('scoring.column.owner')}</span>
          <span className="lq-drawer__figsmall">
            {row.target === null ? (
              <span className="lq-muted">{t('scoring.owner.none')}</span>
            ) : (
              row.target.owner
            )}
          </span>
        </div>
      </div>

      <Tabs
        tabs={tabs}
        active={tab}
        onSelect={onTab}
        label={t('scoring.drawer.tabsLabel')}
      />

      <div className="lq-drawer__panel">{body}</div>

      <div className="lq-drawer__footer">
        <Link
          className="lq-btn lq-btn--primary lq-btn--compact"
          to={`/datasets/${row.id}`}
        >
          {t('scoring.action.investigate')}
        </Link>
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-btn--compact"
          disabled={rerun.isPending}
          onClick={() => {
            rerun.mutate({ datasetIds: [row.id] });
          }}
        >
          {rerun.isPending ? t('scoring.action.rerunning') : t('scoring.action.rerun')}
        </button>
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-btn--compact"
          onClick={() => {
            onTab('runs');
          }}
        >
          {t('scoring.action.compareRuns')}
        </button>
        <span className="lq-drawer__spacer" />
        <Link className="lq-linkish" to="/incidents">
          {t('scoring.action.incidents')}
        </Link>
      </div>

      {rerun.isSuccess && (
        <Alert tone="ok" title={t('scoring.rerun.queuedTitle')}>
          <p className="lq-nomargin">
            {t('scoring.rerun.queuedBody')}{' '}
            <Link className="lq-linkish" to={`/executions/${rerun.data.id}`}>
              {t('scoring.rerun.openExecution')}
            </Link>
          </p>
        </Alert>
      )}
      {rerun.isError && (
        <Alert tone="error" title={t('scoring.rerun.refused')}>
          <p className="lq-nomargin">{rerun.error.message}</p>
        </Alert>
      )}
    </Drawer>
  );
}
